import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from "react-dom";
import {useNavigate, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faListUl, faXmark} from "@fortawesome/free-solid-svg-icons";
import {PageHeader} from "../../components/PageHeader";
import Section from "../../components/section/Section";
import {getAssetByDepartment, getAssetByParentId, getAssetByProject} from "../../api/static/StaticService";
import {CodeSpecNum} from "../../constant/CodeSpecNum";
import {selectCodesByParentId} from "../../api/code/CodeService";
import {getProjectCodeList} from "../../api/projectcode/ProjectCodeService";
import {RecursiveList} from "../../components/recursivelist/RecursiveList";
import {ASSET_CURRENT_HEADER} from "../../components/table/header/Headers";
import {getDepartments} from "../settings/department/DepartmentService";
import AssetTypeStatusChart from "../chart/AssetTypeStatusChart";
import StaticAssetTypeMobileTree from "./StaticAssetTypeMobileTree";
import {simmsColumnsToJsGridHeaders, isMobile} from "../../components/commonmethod/CommonMethod";
import {JsGrid} from "@bavuchoko/js-grid";
import {navigateWithChildTab, pickDetailTabTitle} from "../../components/pageTabs/pageTabsStorage";
import "./staticAssetTypeMobile.css";

function parseJsGridPageable(pageable, prev) {
    const pageNumber = pageable?.pageNumber ?? prev?.currentPage ?? 0;
    const size = pageable?.pageSize ?? pageable?.size ?? prev?.size ?? 10;
    let sort = prev?.sort ?? 'createdAt';
    let desc = prev?.desc ?? true;

    const sortArr = pageable?.sort;
    const token = Array.isArray(sortArr) && sortArr[0] != null ? String(sortArr[0]) : null;
    if (token) {
        const comma = token.indexOf(',');
        if (comma >= 0) {
            sort = token.slice(0, comma).trim();
            desc = token.slice(comma + 1).trim().toLowerCase() === 'desc';
        } else {
            sort = token.trim();
            if (pageable?.sortDirection != null) {
                desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
            }
        }
    } else if (pageable?.sortDirection != null) {
        desc = String(pageable.sortDirection).toUpperCase() === 'DESC';
    }

    return {pageNumber, size, sort, desc};
}

function StaticAssetType() {
    const {type} = useParams();
    const navigate = useNavigate();
    const [chart, setChart] = useState();
    const [list, setList] = useState([]);
    const [code, setCode] = useState();
    const [selected, setSelected] = useState();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        page: 0,
        totalPages: 0,
        totalElements: 0,
        size: 10,
        sort: 'createdAt',
        desc: true,
    });

    const typeId = type === 'group' ? CodeSpecNum.AssetGroupCode
        : type === 'location' ? CodeSpecNum.AssetLocation
            : type === 'task' ? CodeSpecNum.TaskGroupCode
                : null;

    const jsGridHeader = useMemo(
        () => simmsColumnsToJsGridHeaders(ASSET_CURRENT_HEADER),
        []
    );

    const gridData = useMemo(() => ({
        content: list ?? [],
        pageable: {
            pageNumber: pageInfo.currentPage ?? pageInfo.page ?? 0,
            pageSize: pageInfo.size ?? 10,
            size: pageInfo.size ?? 10,
            sort: pageInfo.sort ? [`${pageInfo.sort},${pageInfo.desc ? 'desc' : 'asc'}`] : undefined,
            sortDirection: pageInfo.desc ? 'DESC' : 'ASC',
            totalElements: pageInfo.totalElements,
            totalPages: pageInfo.totalPages,
        },
        totalElements: pageInfo.totalElements,
        totalPages: pageInfo.totalPages,
    }), [list, pageInfo]);

    useEffect(() => {
        setSidebarOpen(false);
    }, [type]);

    useEffect(() => {
        if (type === 'department') {
            getDepartments().then(response => {
                setCode(response.data.content);
            });
            getDepartmentData();
            return;
        }

        if (type === 'project') {
            getProjectCodeList().then(response => {
                const items = response?.data?.content ?? [];
                setCode(items);
                setSelected(null);
                getProjectData();
            });
            return;
        }

        setSelected(typeId);
        selectCodesByParentId(typeId).then(response => {
            setCode(response);
        });
        getData(typeId, typeId);
    }, [type]);

    function applyListResponse(r, reqPageInfo) {
        setChart(r.data.data);
        setList(r.data.list.content ?? []);
        setPageInfo({
            page: r.data.list.pageable.pageNumber,
            currentPage: r.data.list.pageable.pageNumber,
            totalElements: r.data.list.totalElements,
            totalPages: r.data.list.totalPages,
            size: reqPageInfo.size ?? 10,
            sort: reqPageInfo.sort ?? 'createdAt',
            desc: reqPageInfo.desc ?? true,
        });
    }

    function fetchList(value, pageNumber = 0, overrides = {}) {
        const reqPageInfo = {
            ...pageInfo,
            ...overrides,
            page: pageNumber,
            currentPage: pageNumber,
        };
        const request = type === 'department'
            ? getAssetByDepartment(value, reqPageInfo)
            : type === 'project'
                ? getAssetByProject(value, reqPageInfo)
                : getAssetByParentId(typeId, value, reqPageInfo);

        request.then(r => applyListResponse(r, reqPageInfo));
    }

    function getData(typeIdx, value, pageNumber = 0, overrides = {}) {
        fetchList(value ?? typeIdx, pageNumber, overrides);
    }

    function getDepartmentData(value, pageNumber = 0, overrides = {}) {
        fetchList(value, pageNumber, overrides);
    }

    function getProjectData(value, pageNumber = 0, overrides = {}) {
        fetchList(value, pageNumber, overrides);
    }

    const title = {
        'group': '그룹별',
        'task': '업무별',
        'location': '위치별',
        'department': '부서별',
        'project': '시스템별',
    };

    const sidebarTitle = {
        'group': '그룹 목록',
        'task': '업무 목록',
        'location': '위치 목록',
        'department': '부서 목록',
        'project': '시스템 목록',
    };

    const isAllSelected = () => {
        if (type === 'project' || type === 'department') {
            return selected == null;
        }
        return selected == null || selected === typeId;
    };

    function handleViewAll() {
        if (type === 'department') {
            setSelected(null);
            getDepartmentData();
        } else if (type === 'project') {
            setSelected(null);
            getProjectData();
        } else {
            setSelected(typeId);
            getData(typeId, typeId);
        }
        if (isMobile) {
            setSidebarOpen(false);
        }
    }

    function chaneTargetCodeHandler(value) {
        setSelected(value.id);
        if (type === 'department') {
            getDepartmentData(value.id);
        } else if (type === 'project') {
            getProjectData(value.id);
        } else {
            getData(typeId, value.id);
        }
        if (isMobile) {
            setSidebarOpen(false);
        }
    }

    function renderSidebarPanel({showTitle = true, mobile = false} = {}) {
        const listClassName = mobile
            ? "static-asset-type-sheet__tree min-h-0 flex-1 overflow-y-auto"
            : "min-h-0 flex-1 overflow-y-auto p-2";

        return (
            <>
                {showTitle && (
                    <div className="shrink-0 border-b border-[#eef2f7] px-3 pb-2 pt-3">
                        <span className="inline-block border-b-2 border-[#1c90fb] pb-1 text-[14px] font-semibold text-[#333]">
                            {sidebarTitle[type]}
                        </span>
                    </div>
                )}
                <div className={listClassName}>
                    <button
                        type="button"
                        className={
                            mobile
                                ? `static-asset-type-sheet__item-all${isAllSelected() ? " is-selected" : ""}`
                                : `compo-each flex h-[30px] w-full cursor-pointer bg-white${isAllSelected() ? " is-selected" : ""}`
                        }
                        onClick={handleViewAll}
                    >
                        {mobile ? "전체 보기" : <p className="w-full truncate pl-2">전체</p>}
                    </button>
                    {code &&
                        (mobile ? (
                            <StaticAssetTypeMobileTree
                                data={code}
                                selectedId={isAllSelected() ? undefined : selected}
                                onSelect={chaneTargetCodeHandler}
                            />
                        ) : (
                            <RecursiveList data={code} setTarget={chaneTargetCodeHandler} readOnly/>
                        ))
                    }
                </div>
            </>
        );
    }

    const mobileSidebarDrawer = isMobile && sidebarOpen && createPortal(
        <div className="static-asset-type-sheet-root" role="presentation">
            <div
                className="static-asset-type-sheet-overlay"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
            />
            <aside
                className="static-asset-type-sheet"
                role="dialog"
                aria-modal="true"
                aria-label={sidebarTitle[type]}
            >
                <div className="static-asset-type-sheet__handle-wrap">
                    <div className="static-asset-type-sheet__handle" aria-hidden="true"/>
                </div>
                <div className="static-asset-type-sheet__header">
                    <div className="static-asset-type-sheet__title-wrap">
                        <h2 className="static-asset-type-sheet__title">{sidebarTitle[type]}</h2>
                        <p className="static-asset-type-sheet__subtitle">{title[type]} 현황 필터</p>
                    </div>
                    <button
                        type="button"
                        className="static-asset-type-sheet__close"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="목록 닫기"
                    >
                        <FontAwesomeIcon icon={faXmark}/>
                    </button>
                </div>
                <div className="static-asset-type-sheet__body">
                    {renderSidebarPanel({showTitle: false, mobile: true})}
                </div>
            </aside>
        </div>,
        document.body
    );

    const handleGridPageChange = (pageable) => {
        const {pageNumber, size, sort, desc} = parseJsGridPageable(pageable, pageInfo);
        fetchList(selected, pageNumber, {size, sort, desc});
    };

    const handleRowClick = (row) => {
        const id = row?.id;
        if (id == null) return;
        const url = `/asset/${id}`;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(row, "자산상세"),
            url,
        });
    };

    return (
        <div className={`common-container static-asset-type-page ${isMobile ? "" : "container-height-full"}`}>
            <PageHeader header={"자산현황"} indicator={`자산관리 > 자산현황관리 > ${title[type]} 현황`}/>

            <Section className="mb-none flex min-h-0 flex-1 flex-col">
                <div className="static-asset-type-layout flex min-h-0 flex-1 w-full gap-[8px]">
                    <aside className="static-asset-type-sidebar mobile-hidden flex w-[240px] shrink-0 flex-col overflow-hidden border rounded-si bg-white">
                        {renderSidebarPanel()}
                    </aside>

                    <div className="static-asset-type-main flex min-h-0 min-w-0 flex-1 flex-col gap-[8px]">
                        <div className="static-asset-type-chart flex h-[min(280px,32vh)] min-h-[220px] shrink-0 flex-col overflow-hidden border rounded-si bg-white p-[16px]">
                            {chart &&
                                <AssetTypeStatusChart data={chart}/>
                            }
                        </div>

                        <div className="static-asset-type-grid flex min-h-0 flex-1 flex-col overflow-hidden border rounded-si bg-white">
                            <div className="container-height-body min-h-0 flex-1">
                                <JsGrid
                                    resizable={true}
                                    data={gridData}
                                    theme="linear"
                                    style={{minHeight: 0, height: '100%'}}
                                    header={jsGridHeader}
                                    onRowClick={handleRowClick}
                                    onPageChange={handleGridPageChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {isMobile && (
                <button
                    type="button"
                    className="static-asset-type-fab web-hidden fixed bottom-[20px] left-[20px] z-[45]"
                    onClick={() => setSidebarOpen(true)}
                    title={sidebarTitle[type]}
                    aria-label={sidebarTitle[type]}
                >
                    <span className="static-asset-type-fab__icon">
                        <FontAwesomeIcon icon={faListUl}/>
                    </span>
                    <span className="static-asset-type-fab__label">{sidebarTitle[type]}</span>
                </button>
            )}

            {mobileSidebarDrawer}
        </div>
    );
}

export default StaticAssetType;
